import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {map, mergeMap, catchError, withLatestFrom} from 'rxjs/operators';
import {forkJoin, Observable, of} from 'rxjs';
import {ApiService} from '../../../core/http/api.service';
import moment from 'moment';
import {ISO_FROM_BACKEND, YMD_FORMAT} from '../../../core/constants/date-and-time-formats.constants';
import {Store} from '@ngrx/store';
import * as fromCalendar from '../../../store/reducers/calendar';
import {
  GetPropertyAdditionalInformationResponseModel,
  GetPropertyViewingAppointmentsResponseModel,
  PropertyViewingAppointmentModel
} from '../../../shared/models/properties/properties.models';
import {
  GetPropertyViewingAvailabilitiesResponseModel,
  GetPropertyViewingAvailabilityResponseModel
} from '../../../shared/models/properties/property-availability.models';
import {GetUserViewingAvailabilitiesResponseModel} from '../../../shared/models/users/user-availability.models';
import {GetUserAppointmentsResponseModel} from '../../../shared/models/users/users.models';
import {CalendarWidgetService} from '../../../shared/services/calendar-widget.service';
import {CcPermissionsService} from '../../../core/services/permission/cc/cc-permissions.service';
import { CalendarWidgetActions } from '../../actions/calendar/calendar-widget.actions';
import {AppointmentModel} from '../../../shared/models/calendar/appointments.models';
import {AppointmentType} from '../../../shared/constants/appointmentTypes.constants';

@Injectable()
export class CalendarWidgetEffects {

  initCalendarWidget = createEffect((): Observable<any> => this.actions$
  .pipe(
    ofType(CalendarWidgetActions.initCalendarWidget),
    withLatestFrom(this.store.select(fromCalendar.selectCalendarWidgetState)),
    mergeMap(([data, store]) =>
      this.api.Properties.getPropertyAdditionalInfo(store.propertyId, {
        fields: ['hosting-viewings-user-id', 'is-seller-hosting-viewings']
      }).pipe(
        mergeMap((res: GetPropertyAdditionalInformationResponseModel) => {
          const defaultLogic: object[] = [
            CalendarWidgetActions.setHostId({id: res.data.hostingViewingsUserId}),
            CalendarWidgetActions.setIsHostedBySeller({status: res.data.isSellerHostingViewings}),
            CalendarWidgetActions.loadPropertyFirstViewingAvailability(),
          ];

          if (res.data.isSellerHostingViewings) {
            defaultLogic.push(CalendarWidgetActions.loadUserViewingAvailability());
          }

          return defaultLogic;
        }),
      )
    )
  ));

   loadPropertyFirstViewingAvailability = createEffect(() => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.loadPropertyFirstViewingAvailability),
      withLatestFrom(this.store.select(fromCalendar.selectCalendarWidgetState)),
      mergeMap(([data, store]) =>
        this.api.Properties.Availability.getPropertyFirstViewingAvailability(store.propertyId, store.initialSettings).pipe(
          mergeMap((res: GetPropertyViewingAvailabilityResponseModel) => [
            CalendarWidgetActions.setSelectedDate({date: res.data.startDateTime}),
            CalendarWidgetActions.loadPropertyViewingAvailability()
          ]),
          catchError(() => of(CalendarWidgetActions.loadPropertyViewingAvailabilityFailure()))
        )
      )
    ));

   loadPropertyViewingAvailability = createEffect((): Observable<any> => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.loadPropertyViewingAvailability),
      withLatestFrom(this.store.select(fromCalendar.selectCalendarWidgetState)),
      mergeMap(([data, store]) => {
        const selectedDate = moment(store.selectedDate, ISO_FROM_BACKEND);
        const candidateStart = selectedDate.clone().subtract(1, 'day');
        const visibleWeeksStart = store.selectedWeeks?.startDate
          ? moment(store.selectedWeeks.startDate, ISO_FROM_BACKEND)
          : null;
        const appointmentsStartDate = (visibleWeeksStart && visibleWeeksStart.isBefore(candidateStart))
          ? visibleWeeksStart.format(YMD_FORMAT)
          : candidateStart.format(YMD_FORMAT);
        const appointmentsEndDate = selectedDate.clone().add(8, 'weeks').format(YMD_FORMAT);

        return forkJoin({
          availability: this.api.Properties.Availability.getPropertyViewingAvailabilitiesGuest(store.propertyId, {
            'start-date': moment(store.selectedDate).format(YMD_FORMAT),
            'end-date': moment(store.selectedDate).format(YMD_FORMAT),
            'slot-duration': store.slotDuration,
            'availability-duration': store.availabilityDuration
          }),
          propertyViewingAppointments: this.api.Properties.getPropertyViewingAppointments(store.propertyId, {
            'start-date': appointmentsStartDate,
            'end-date': appointmentsEndDate,
            fields: ['auto-confirm-slots-relation', 'viewing-id', 'buyer-id', 'appointment-start', 'appointment-end', 'status', 'start', 'property-id', 'agent-id', 'appointment-id', 'viewing-status-id']
          }).pipe(
            catchError(() => of({data: []}))
          )
        }).pipe(
          mergeMap(({availability, propertyViewingAppointments}) => {
            const transformedData = Array.isArray(availability.data) && availability.data.length > 0 && !Array.isArray(availability.data[0])
              ? (availability.data as any).map((slot: any) => {
                  if (slot.intervals && Array.isArray(slot.intervals) && slot.intervals.length > 0) {
                    return [{
                      ...slot,
                      subSlots: slot.intervals
                    }];
                  }
                  return [slot];
                })
              : availability.data;

            const transformedRes: GetPropertyViewingAvailabilitiesResponseModel = {
              ...availability,
              data: transformedData as any
            };

            const defaultLogic: object[] = [CalendarWidgetActions.setPropertyViewingAvailability(transformedRes)];

            if (propertyViewingAppointments.data.length > 0) {
              const convertedAppointments = this.mergeAppointmentsWithPropertyViewings(
                [],
                propertyViewingAppointments.data
              );
              defaultLogic.push(CalendarWidgetActions.processUserAppointments({data: convertedAppointments}));
            }

            defaultLogic.push(CalendarWidgetActions.loadUserAppointments());

            return defaultLogic;
          }),
          catchError(() => of())
        );
      })
    ));

   loadPropertyViewingAvailabilityFailure = createEffect(() => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.loadPropertyViewingAvailabilityFailure),
      mergeMap(() => [
        CalendarWidgetActions.setSelectedDate({date: moment().add(1, 'day').toISOString()}),
        CalendarWidgetActions.loadPropertyViewingAvailability()
      ])
    ));

   setCalendarWidgetDate = createEffect(() => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.setCalendarWidgetDate),
      map(() => CalendarWidgetActions.loadPropertyViewingAvailability())
    ));

   loadUserViewingAvailability = createEffect((): Observable<any> => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.loadUserViewingAvailability),
      withLatestFrom(this.store.select(fromCalendar.selectCalendarWidgetState)),
      mergeMap(([data, store]) =>
        this.api.Users.Availability.getUserViewingAvailabilities(store.hostId).pipe(
          map((res: GetUserViewingAvailabilitiesResponseModel) => CalendarWidgetActions.setUserViewingAvailability(res)),
          catchError(() => of())
        )
      )
    ));

   loadUserAppointments = createEffect((): Observable<any> => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.loadUserAppointments),
      withLatestFrom(this.store.select(fromCalendar.selectCalendarWidgetState)),
      mergeMap(([data, store]) => {
        const selectedDate = moment(store.selectedDate, ISO_FROM_BACKEND);
        const candidatePropertyStart = selectedDate.clone().subtract(1, 'day');
        const visibleWeeksStart = store.selectedWeeks?.startDate
          ? moment(store.selectedWeeks.startDate, ISO_FROM_BACKEND)
          : null;
        const propertyStartDate = (visibleWeeksStart && visibleWeeksStart.isBefore(candidatePropertyStart))
          ? visibleWeeksStart.format(YMD_FORMAT)
          : candidatePropertyStart.format(YMD_FORMAT);

        return forkJoin({
          userAppointments: this.api.Users.getUserAppointments(store.hostId, {
            'start-datetime': selectedDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            'end-datetime': selectedDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            fields: ['related', 'auto-confirm-slots-relation', 'start', 'status', 'viewing-id', 'buyer-id', 'appointment-start', 'appointment-end', 'property-id', 'agent-id']
          }).pipe(catchError(() => of({data: []}))),
          propertyViewingAppointments: this.api.Properties.getPropertyViewingAppointments(store.propertyId, {
            'start-date': propertyStartDate,
            'end-date': selectedDate.clone().add(8, 'weeks').format(YMD_FORMAT),
            fields: ['auto-confirm-slots-relation', 'viewing-id', 'buyer-id', 'appointment-start', 'appointment-end', 'status', 'start', 'property-id', 'agent-id', 'appointment-id', 'viewing-status-id']
          }).pipe(catchError(() => of({data: []})))
        }).pipe(
          mergeMap(({userAppointments, propertyViewingAppointments}) => {
            const mergedAppointments = this.mergeAppointmentsWithPropertyViewings(
              userAppointments.data,
              propertyViewingAppointments.data
            );
            return [CalendarWidgetActions.processUserAppointments({data: mergedAppointments})];
          }),
          catchError(() => of())
        );
      })
    ));

   loadUserAppointmentsForSelectedWeeks = createEffect((): Observable<any> => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.loadUserAppointmentsForSelectedWeeks),
      withLatestFrom(this.store.select(fromCalendar.selectCalendarWidgetState)),
      mergeMap(([data, store]) => {
        const startDate = moment(store.selectedWeeks.startDate, ISO_FROM_BACKEND).startOf('day').format('YYYY-MM-DD HH:mm:ss');
        const endDate = moment(store.selectedWeeks.endDate, ISO_FROM_BACKEND).endOf('day').format('YYYY-MM-DD HH:mm:ss');

        return forkJoin({
          userAppointments: this.api.Users.getUserAppointments(store.hostId, {
            'start-datetime': startDate,
            'end-datetime': endDate,
            fields: ['related', 'auto-confirm-slots-relation', 'start', 'status', 'viewing-id', 'buyer-id', 'appointment-start', 'appointment-end', 'property-id', 'agent-id']
          }).pipe(catchError(() => of({data: []}))),
          propertyViewingAppointments: this.api.Properties.getPropertyViewingAppointments(store.propertyId, {
            'start-date': moment(store.selectedWeeks.startDate, ISO_FROM_BACKEND).format(YMD_FORMAT),
            'end-date': moment(store.selectedWeeks.endDate, ISO_FROM_BACKEND).format(YMD_FORMAT),
            fields: ['agent-id', 'auto-confirm-slots-relation', 'viewing-id', 'buyer-id', 'appointment-start', 'appointment-end', 'property-id', 'status', 'start', 'appointment-id', 'viewing-status-id']
          }).pipe(catchError(() => of({data: []})))
        }).pipe(
          mergeMap(({userAppointments, propertyViewingAppointments}) => {
            const mergedAppointments = this.mergeAppointmentsWithPropertyViewings(
              userAppointments.data,
              propertyViewingAppointments.data
            );
            return [CalendarWidgetActions.processUserAppointments({data: mergedAppointments})];
          }),
          catchError(() => of())
        );
      })
    ));

   processUserAppointments = createEffect((): Observable<any> => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.processUserAppointments),
      mergeMap(res => {
          const defaultLogic: object[] = [];
          if (res && res.data && res.data.length) {
            defaultLogic.push(CalendarWidgetActions.loadUserAppointmentsSuccess(res));
            this.calendarWidgetService.assignAppointmentsToCorrectTimeSlots(res.data);
          }
          return defaultLogic;
        }
      )
    ));

   loadHostDetails = createEffect((): Observable<any> => this.actions$
    .pipe(
      ofType(CalendarWidgetActions.setHostId),
      mergeMap((action) =>
        this.api.Users.getUserDetailsById(action.id, {
          fields: ['auto-confirm-enabled']
        }).pipe(
          map((hostResponse: any) => CalendarWidgetActions.setHostDetails({
            hostDetails: hostResponse.data
          })),
          catchError(() => of())
        )
      )
    ));

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private store: Store,
    private calendarWidgetService: CalendarWidgetService,
    private ccPermissionsService: CcPermissionsService
  ) {
  }

  private mergeAppointmentsWithPropertyViewings(
    userAppointments: AppointmentModel[],
    propertyViewings: PropertyViewingAppointmentModel[]
  ): AppointmentModel[] {
    const existingViewingIds = new Set(
      userAppointments
        .filter(apt => apt.relatedType === AppointmentType.VIEWINGS)
        .map(apt => apt.related?.id)
        .filter(id => id !== undefined)
    );

    const convertedPropertyViewings: AppointmentModel[] = propertyViewings
      .filter(viewing => !existingViewingIds.has(viewing.id))
      .filter(viewing => viewing.appointmentStart && viewing.appointmentEnd)
      .map(viewing => ({
        id: viewing.id,
        appointmentId: viewing.appointmentId,
        appointmentStart: viewing.appointmentStart || '',
        appointmentEnd: viewing.appointmentEnd || '',
        bookedBy: 0,
        cancelledAt: viewing.cancelledAt ?? null,
        completedAt: null,
        createdAt: '',
        updatedAt: '',
        relatedType: AppointmentType.VIEWINGS,
        related: {
          id: viewing.id,
          propertyId: viewing.propertyId,
          agentId: viewing.agentId,
          initialAgentId: viewing.initialAgentId,
          viewingStatusId: viewing.viewingStatusId,
          autoConfirmSlotsRelation: viewing.autoConfirmSlotsRelation?.map(slot => ({
            id: slot.id,
            start: slot.start,
            status: slot.status,
            viewingId: slot.viewingId,
            buyerId: slot.buyerId ?? null,
            agentId: viewing.agentId,
            propertyId: viewing.propertyId
          })) ?? []
        }
      } as AppointmentModel));

    const taggedUserAppointments: AppointmentModel[] = userAppointments.map(apt => ({
      ...apt,
      appointmentId: apt.appointmentId ?? apt.id,
    }));

    return [...taggedUserAppointments, ...convertedPropertyViewings];
  }
}
